import { createClient } from '@supabase/supabase-js';
import { applyNetlifyRealtimeFallback, resolveBrowserSupabaseUrl, rewritePublicStorageUrl } from '../utils/supabaseBrowserUrl';
import LoggerService from './LoggerService';
import { getProductItemFeeMmkForPackage } from '../utils/parseMerchantProductFee';
import type {
  Product,
  ProductVariant,
  ProductPendingUpdate,
} from './_shared/productReview';
import {
  isProductLiveApproved,
  hasPendingProductUpdate,
  productFormSource,
  productNeedsAdminReview,
  pickProductReviewSnapshot,
  buildPendingUpdateFromProduct,
  normalizePendingPayload,
  toDirectProductPatch,
} from './_shared/productReview';
import { buildPricingSettings, pricingFieldToCamel } from './_shared/pricing';
import type {
  Banner,
  Tutorial,
  ProductCategory,
  StoreReview,
  RechargeRequest,
} from './_shared/domainTypes';
import { createBannerService, createTutorialService } from './_shared/services';
import { isMissingPackingStartedAtColumn } from './_shared/packingCountdown';
import {
  buildCustomerPhoneOrFilter,
  mergePackageRows,
} from './_shared/customerPackageQuery';
export type { Banner, Tutorial, ProductCategory, StoreReview, RechargeRequest };

// 使用环境变量配置 Supabase（不再使用硬编码密钥）
const supabaseUrl = resolveBrowserSupabaseUrl(process.env.REACT_APP_SUPABASE_URL);
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// 验证 API key 是否有效
if (!supabaseUrl || !supabaseKey) {
    LoggerService.error('❌ 错误：Supabase 环境变量未配置！');
    LoggerService.error('本地开发：在本项目目录执行 cp .env.example .env.local，填写 URL 与 ANON_KEY 后重新 npm start');
    LoggerService.error('线上部署：在 Netlify → Site settings → Environment variables 中配置：');
    LoggerService.error('  - REACT_APP_SUPABASE_URL');
    LoggerService.error('  - REACT_APP_SUPABASE_ANON_KEY');
    throw new Error(
      '请配置 REACT_APP_SUPABASE_URL 与 REACT_APP_SUPABASE_ANON_KEY（本地用 .env.local，见 .env.example）',
    );
}

export const supabase = createClient(supabaseUrl, supabaseKey);
// production browser uses /__sb; realtime WS falls back to Worker
applyNetlifyRealtimeFallback(supabase);

// 包裹数据类型定义
export interface Package {
  id: string;
  sender_name: string;
  sender_phone: string;
  sender_address: string;
  sender_latitude?: number;
  sender_longitude?: number;
  receiver_name: string;
  receiver_phone: string;
  receiver_address: string;
  receiver_latitude?: number;
  receiver_longitude?: number;
  package_type: string;
  weight: string;
  description?: string;
  delivery_speed?: string;
  scheduled_delivery_time?: string;
  delivery_distance?: number;
  status: string;
  create_time: string;
  pickup_time: string;
  delivery_time: string;
  courier: string;
  price: string;
  region?: string;
  created_at?: string;
  updated_at?: string;
  delivery_store_id?: string;
  delivery_store_name?: string;
  store_receive_code?: string;
  sender_code?: string;
  transfer_code?: string;
  payment_method?: 'qr' | 'cash' | 'balance'; // 🚀 支付方式：qr=二维码支付，cash=现金支付, balance=余额支付
  cod_amount?: number; // 代收款金额 (Cash on Delivery)
  customer_id?: string; // 客户ID
  customer_email?: string; // 客户邮箱
  customer_name?: string; // 客户姓名
  cod_settled?: boolean; // 代收款是否已结清
  cod_settled_at?: string; // 代收款结清时间
  /** 结清方：admin | merchant；历史单可能为空 */
  cod_settled_by?: string;
  cod_settled_by_id?: string;
  cod_settled_by_name?: string;
  pricing_base_fee_mmk?: number | null;
  packing_started_at?: string | null;
}

// 广告横幅接口
// Banner/Tutorial/ProductCategory/StoreReview/RechargeRequest 已抽到 /shared/src/domainTypes.ts（见顶部 import）

// 配送店接口
export interface DeliveryStore {
  id: string;
  store_name: string;
  store_code?: string;
  address: string;
  phone: string;
  manager_phone?: string;
  email?: string;
  store_type: string;
  status: string;
  operating_hours?: string;
  is_closed_today?: boolean;
  latitude?: number;
  longitude?: number;
  created_at?: string;
  updated_at?: string;
  vacation_dates?: string[]; // 🚀 新增：休假日期列表 (YYYY-MM-DD)
  cod_settlement_day?: '7' | '10' | '15' | '30'; // 🚀 新增：COD 结清日
  avatar_url?: string | null;
  packing_sla_minutes?: number | null;
}

// 商品接口
// 商品类型与上架审核辅助函数已抽到 /shared/src/productReview.ts（见顶部 import），此处仅再导出
export type { Product, ProductVariant, ProductPendingUpdate };
export {
  isProductLiveApproved,
  hasPendingProductUpdate,
  productFormSource,
  productNeedsAdminReview,
  pickProductReviewSnapshot,
  buildPendingUpdateFromProduct,
  normalizePendingPayload,
  toDirectProductPatch,
};

/**
 * 拆查订单：邮箱、客户ID 描述、电话不要拼进同一段 PostgREST `.or()`。
 */
async function fetchPackagesByUserSplit(opts: {
  email?: string;
  phone?: string;
  startDate?: string;
  storeId?: string;
  userId?: string;
  storeName?: string;
}): Promise<Package[]> {
  const { email, phone, startDate, storeId, userId, storeName } = opts;
  if (!email && !phone && !storeId && !userId && !storeName) {
    return [];
  }

  const applyDate = (query: any) =>
    startDate ? query.gte('created_at', startDate) : query;
  const runSelect = () => applyDate(supabase.from('packages').select('*'));

  const run = async (
    build: (query: any) => any,
    ignore?: (message: string) => boolean,
  ): Promise<Package[]> => {
    try {
      const { data, error } = await build(runSelect()).order('created_at', {
        ascending: false,
      });
      if (error) {
        const message = String(error.message || '');
        if (!ignore?.(message)) {
          LoggerService.error('查询订单失败:', error);
        }
        return [];
      }
      return (data || []) as Package[];
    } catch (err) {
      LoggerService.error('查询订单失败:', err);
      return [];
    }
  };

  const tasks: Array<Promise<Package[]>> = [];

  if (userId) {
    tasks.push(
      run(
        (q) => q.eq('customer_id', userId),
        (message) =>
          message.includes('customer_id') && message.includes('does not exist'),
      ),
    );
    tasks.push(
      run((q) => q.ilike('description', `%[客户ID: ${userId}]%`)),
    );
  }

  const emailVal = String(email || '').trim();
  if (emailVal) {
    tasks.push(
      run(
        (q) => q.eq('customer_email', emailVal),
        (message) => message.includes('customer_email'),
      ),
    );
  }

  const phoneFilter = buildCustomerPhoneOrFilter(phone);
  if (phoneFilter) {
    tasks.push(run((q) => q.or(phoneFilter)));
  }

  if (storeId) {
    tasks.push(run((q) => q.eq('delivery_store_id', storeId)));
  }
  if (storeName) {
    tasks.push(run((q) => q.eq('sender_name', storeName)));
  }

  if (!tasks.length) return [];
  return mergePackageRows(await Promise.all(tasks));
}

// 客户端包裹服务（只包含客户端需要的功能）
export const packageService = {
  // 获取所有包裹（用于跟踪页面）
  async getAllPackages(): Promise<Package[]> {
    try {
      const { data, error } = await supabase
        .from('packages')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        LoggerService.error('获取包裹列表失败:', error);
        return [];
      }
      
      return data || [];
    } catch (err) {
      LoggerService.error('获取包裹列表异常:', err);
      return [];
    }
  },

  // 创建新包裹
  async createPackage(packageData: Omit<Package, 'id' | 'created_at' | 'updated_at'>): Promise<Package | null> {
    try {
      LoggerService.debug('尝试创建包裹:', packageData);
      
      // 如果 customer_email 或 customer_name 字段不存在，从数据中移除它们
      // 这样可以避免数据库列不存在的错误
      const dataToInsert: any = { ...packageData };
      
      // 检查并处理可能不存在的字段
      // 如果数据库表中没有这些字段，尝试插入时会失败
      // 所以我们先尝试插入，如果失败则移除这些字段重试
      let { data, error } = await supabase
        .from('packages')
        .insert([dataToInsert])
        .select()
        .single();
      
      // 如果错误是因为列不存在，移除这些字段后重试
      if (error && (
        error.message.includes('customer_email') || 
        error.message.includes('customer_name') || 
        error.message.includes('sender_code') || 
        error.message.includes('delivery_store_id') || 
        error.message.includes('delivery_store_name') || 
        error.message.includes('cod_amount') ||
        error.message.includes('pricing_base_fee') ||
        error.code === 'PGRST204'
      )) {
        LoggerService.warn('检测到列不存在，尝试移除可选字段后重试:', error.message);
        
        // 如果是特定字段错误，只移除该字段；如果是通用错误(PGRST204)，移除所有可能的新字段
        if (error.message.includes('customer_')) {
          delete dataToInsert.customer_email;
          delete dataToInsert.customer_name;
        }
        if (error.message.includes('sender_code') || error.message.includes('delivery_store_')) {
          delete dataToInsert.sender_code;
          delete dataToInsert.delivery_store_id;
          delete dataToInsert.delivery_store_name;
        }
        if (error.message.includes('cod_amount')) {
          delete dataToInsert.cod_amount;
        }
        if (error.message.includes('pricing_base_fee')) {
          delete dataToInsert.pricing_base_fee_mmk;
        }
        
        // 如果是通用错误，移除所有可能导致问题的可选字段
        if (error.code === 'PGRST204') {
          delete dataToInsert.customer_email;
          delete dataToInsert.customer_name;
          delete dataToInsert.sender_code;
          delete dataToInsert.delivery_store_id;
          delete dataToInsert.delivery_store_name;
          delete dataToInsert.cod_amount;
          delete dataToInsert.pricing_base_fee_mmk;
        }
        
        // 重试插入
        const retryResult = await supabase
          .from('packages')
          .insert([dataToInsert])
          .select()
          .single();
        
        if (retryResult.error) {
          error = retryResult.error;
        } else {
          data = retryResult.data;
          error = null;
        }
      }
      
      if (error) {
        LoggerService.error('【Supabase错误】创建包裹失败:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
        });
        
        // 检查是否是 API key 错误
        if (error.message && error.message.includes('Invalid API key')) {
          const errorMsg = `API Key 配置错误：${error.message}\n\n` +
            `请检查 Netlify Dashboard 中的环境变量配置：\n` +
            `1. 访问 Netlify Dashboard → Site settings → Environment variables\n` +
            `2. 确认已配置 REACT_APP_SUPABASE_ANON_KEY\n` +
            `3. 确认 API Key 值正确且完整\n` +
            `4. 重新部署网站以应用更改`;
          throw new Error(errorMsg);
        }
        
        throw new Error(`数据库错误: ${error.message} (代码: ${error.code || '未知'})`);
      }
      
      LoggerService.debug('包裹创建成功:', data);
      return data;
    } catch (err: any) {
      LoggerService.error('【服务层异常】创建包裹时发生未知错误:', err);
      throw err;
    }
  },

  // 根据ID获取包裹（用于跟踪）
  async getPackageById(id: string): Promise<Package | null> {
    try {
      const { data, error } = await supabase
        .from("packages")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        LoggerService.error("获取包裹详情失败:", error);
        return null;
      }

      return data;
    } catch (err) {
      LoggerService.error("获取包裹详情异常:", err);
      return null;
    }
  },

  // 计算两点之间的距离 (Haversine 公式)
  calculateDistance(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number,
  ): number {
    const R = 6371; // 地球半径 (km)
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLng = (lng2 - lng1) * (Math.PI / 180);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * (Math.PI / 180)) *
        Math.cos(lat2 * (Math.PI / 180)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return parseFloat((R * c).toFixed(2));
  },

  // 根据订单号搜索包裹（支持模糊搜索）
  async searchPackage(trackingNumber: string): Promise<Package | null> {
    try {
      // 先尝试精确匹配
      let { data, error } = await supabase
        .from('packages')
        .select('*')
        .eq('id', trackingNumber.toUpperCase())
        .single();
      
      if (error || !data) {
        // 如果精确匹配失败，尝试模糊搜索
        const { data: searchData, error: searchError } = await supabase
          .from('packages')
          .select('*')
          .ilike('id', `%${trackingNumber.toUpperCase()}%`)
          .limit(1)
          .single();
        
        if (searchError || !searchData) {
          return null;
        }
        return searchData;
      }
      
      return data;
    } catch (err) {
      LoggerService.error('搜索包裹异常:', err);
      return null;
    }
  },

  // 根据用户邮箱或手机号获取该用户的所有包裹
  // startDate: 可选，如果有值，只查询该时间之后的订单
  // storeId: 可选，如果是商家账号，同时查询该商家关联的订单
  // userId: 可选，如果有值，通过 customer_id 匹配
  // storeName: 可选，如果是商家账号，通过 sender_name 匹配
  async getPackagesByUser(email?: string, phone?: string, startDate?: string, storeId?: string, userId?: string, storeName?: string): Promise<Package[]> {
    try {
      if (!email && !phone && !storeId && !userId && !storeName) {
        LoggerService.debug('getPackagesByUser: 没有查询标识，返回空数组');
        return [];
      }
      LoggerService.debug('getPackagesByUser: 开始查询，email:', email, 'phone:', phone, 'startDate:', startDate, 'storeId:', storeId, 'userId:', userId, 'storeName:', storeName);
      const rows = await fetchPackagesByUserSplit({
        email,
        phone,
        startDate,
        storeId,
        userId,
        storeName,
      });
      LoggerService.debug('查询成功，包裹数量:', rows.length);
      return rows;
    } catch (err) {
      LoggerService.error('获取用户包裹列表异常:', err);
      return [];
    }
  },

  /** 商家店铺订单列表（按 delivery_store_id，避免宽泛 OR 查询拖慢页面） */
  async getPackagesByStore(
    storeId: string,
    options?: { limit?: number },
  ): Promise<Package[]> {
    if (!storeId) return [];
    try {
      let query = supabase
        .from('packages')
        .select(
          'id,status,sender_name,sender_phone,sender_address,receiver_name,receiver_phone,receiver_address,package_type,weight,description,price,payment_method,cod_amount,created_at,create_time,delivery_store_id,courier',
        )
        .eq('delivery_store_id', storeId)
        .order('created_at', { ascending: false });

      if (options?.limit && options.limit > 0) {
        query = query.limit(options.limit);
      }

      const { data, error } = await query;
      if (error) {
        LoggerService.error('getPackagesByStore failed:', error);
        return [];
      }
      return (data || []) as Package[];
    } catch (err) {
      LoggerService.error('getPackagesByStore exception:', err);
      return [];
    }
  },

  // 获取合伙店铺代收款统计
  async getPartnerStats(userId: string, storeName?: string, month?: string) {
    try {
      // 构建查询函数
      const runQuery = async (fields: string) => {
        let q = supabase.from('packages').select(fields).eq('status', '已送达');

        const conditions = [`delivery_store_id.eq.${userId}`];
        if (storeName) {
          conditions.push(`sender_name.eq.${storeName}`);
        }
        
        q = q.or(conditions.join(','));
        
        // 如果指定了月份，添加日期过滤
        if (month) {
          const [year, monthNum] = month.split('-');
          const startDate = `${year}-${monthNum}-01`;
          const endDate = new Date(parseInt(year), parseInt(monthNum), 0).toISOString().split('T')[0];
          q = q.gte('delivery_time', startDate).lte('delivery_time', endDate);
        }
        
        return q;
      };

      // 尝试查询所有字段
      let { data, error } = await runQuery('cod_amount, cod_settled, cod_settled_at, status, delivery_time');

      // 如果报错字段不存在 (42703)，降级查询（不查 cod_settled 相关字段）
      if (error && error.code === '42703') {
        LoggerService.warn('cod_settled 字段不存在，使用降级查询');
        const retryResult = await runQuery('cod_amount, status, delivery_time');
        data = retryResult.data;
        error = retryResult.error;
      }

      if (error) throw error;

      // 类型断言：确保 data 是正确的类型
      const packages = (data || []) as Array<{
        cod_amount?: number;
        cod_settled?: boolean;
        cod_settled_at?: string;
        status?: string;
        delivery_time?: string;
      }>;

      const totalCOD = packages.reduce((sum, pkg) => sum + (pkg.cod_amount || 0), 0);
      
      // 如果没有 cod_settled 字段，data 中该属性为 undefined，!undefined 为 true，即默认未结清
      const unclearedPackages = packages.filter(pkg => !pkg.cod_settled);
      const unclearedCOD = unclearedPackages.reduce((sum, pkg) => sum + (pkg.cod_amount || 0), 0);
      const unclearedCount = unclearedPackages.length;
      
      // 「当年已结清」与 Admin 财务管理一致：按 cod_settled_at 自然年，而非按「送达月」
      const storeOrConds = [`delivery_store_id.eq.${userId}`];
      if (storeName) {
        storeOrConds.push(`sender_name.eq.${storeName}`);
      }
      const y = new Date().getFullYear();
      const yStart = new Date(y, 0, 1).toISOString();
      const yEnd = new Date(y, 11, 31, 23, 59, 59, 999).toISOString();
      let yq = supabase
        .from('packages')
        .select('cod_amount, cod_settled, cod_settled_at')
        .in('status', ['已送达', '已完成'])
        .or(storeOrConds.join(','))
        .eq('cod_settled', true)
        .not('cod_settled_at', 'is', null)
        .gte('cod_settled_at', yStart)
        .lte('cod_settled_at', yEnd);
      const { data: yData, error: yError } = await yq;
      if (yError) {
        LoggerService.warn('当年已结清统计查询失败，可能缺少 cod_settled_at：', yError);
      }
      const yearPkgs = (yData || []) as Array<{
        cod_amount?: number;
        cod_settled?: boolean;
        cod_settled_at?: string;
      }>;
      const settledCOD = yearPkgs.reduce((sum, pkg) => sum + (pkg.cod_amount || 0), 0);
      const settledPackagesWithDate = yearPkgs.filter(pkg => pkg.cod_settled_at);
      let lastSettledAt: string | null = null;
      if (settledPackagesWithDate.length > 0) {
        settledPackagesWithDate.sort(
          (a, b) =>
            new Date(b.cod_settled_at!).getTime() - new Date(a.cod_settled_at!).getTime(),
        );
        lastSettledAt = settledPackagesWithDate[0].cod_settled_at || null;
      }

      return {
        totalCOD,
        unclearedCOD,
        unclearedCount,
        settledCOD,
        lastSettledAt
      };
    } catch (error) {
      LoggerService.error('获取合伙人统计失败:', error);
      return {
        totalCOD: 0,
        unclearedCOD: 0,
        unclearedCount: 0,
        settledCOD: 0,
        lastSettledAt: null
      };
    }
  },

  /** 待结清：按月份+送达时间；已结清：按当前自然年 cod_settled_at（与 Admin、getPartnerStats 一致） */
  async getPartnerCODOrders(userId: string, storeName?: string, month?: string, settled?: boolean, page: number = 1, pageSize: number = 20) {
    try {
      const conditions = [`delivery_store_id.eq.${userId}`];
      if (storeName) {
        conditions.push(`sender_name.eq.${storeName}`);
      }

      const y = new Date().getFullYear();
      const yStart = new Date(y, 0, 1).toISOString();
      const yEnd = new Date(y, 11, 31, 23, 59, 59, 999).toISOString();
      const selectWithActor =
        'id, cod_amount, delivery_time, cod_settled, cod_settled_at, price, cod_settled_by, cod_settled_by_id, cod_settled_by_name';
      const selectFallback =
        'id, cod_amount, delivery_time, cod_settled, cod_settled_at, price';

      const runQuery = async (selectCols: string) => {
        let q: any;
        if (settled === true) {
          q = supabase
            .from('packages')
            .select(selectCols, { count: 'exact' })
            .in('status', ['已送达', '已完成'])
            .or(conditions.join(','))
            .eq('cod_settled', true)
            .not('cod_settled_at', 'is', null)
            .gte('cod_settled_at', yStart)
            .lte('cod_settled_at', yEnd);
        } else {
          q = supabase
            .from('packages')
            .select(selectCols, { count: 'exact' })
            .eq('status', '已送达')
            .or(conditions.join(','));
          if (settled === false) {
            q = q.or('cod_settled.eq.false,cod_settled.is.null');
          }
          if (month) {
            const [yr, monthNum] = month.split('-');
            const startDate = `${yr}-${monthNum}-01`;
            const endDate = new Date(parseInt(yr), parseInt(monthNum), 0).toISOString().split('T')[0];
            q = q.gte('delivery_time', startDate).lte('delivery_time', endDate);
          }
        }

        const effectivePageSize = settled === true ? Math.max(pageSize, 5000) : pageSize;
        const from = (page - 1) * effectivePageSize;
        const to = from + effectivePageSize - 1;
        return q
          .order(settled === true ? 'cod_settled_at' : 'delivery_time', {
            ascending: false,
          })
          .range(from, to);
      };

      let { data, error, count } = await runQuery(selectWithActor);
      if (error && (
        error.code === 'PGRST204' ||
        error.code === '42703' ||
        String(error.message || '').includes('cod_settled_by')
      )) {
        ({ data, error, count } = await runQuery(selectFallback));
      }

      if (error) throw error;
      
      const orders = (data || []).map((pkg: any) => ({
        orderId: pkg.id,
        codAmount: pkg.cod_amount || 0,
        deliveryTime: pkg.delivery_time,
        deliveryFeeLabel: pkg.price || '',
        settledAt: pkg.cod_settled_at || '',
        settledBy: pkg.cod_settled_by || '',
        settledByName: pkg.cod_settled_by_name || '',
      }));

      return { orders, total: count || 0 };
    } catch (error) {
      LoggerService.error('获取代收款订单列表失败:', error);
      return { orders: [], total: 0 };
    }
  },

  // 🚀 新增：更新包裹状态
  async updatePackageStatus(packageId: string, status: string, additionalData: any = {}): Promise<boolean> {
    try {
      const body: Record<string, unknown> = {
        status,
        updated_at: new Date().toISOString(),
        ...additionalData,
      };
      const { error } = await supabase.from('packages').update(body).eq('id', packageId);
      if (error && isMissingPackingStartedAtColumn(error) && additionalData?.packing_started_at) {
        const retry = { ...body };
        delete retry.packing_started_at;
        const second = await supabase.from('packages').update(retry).eq('id', packageId);
        if (second.error) throw second.error;
        return true;
      }
      if (error) throw error;
      return true;
    } catch (err) {
      LoggerService.error('更新包裹状态失败:', err);
      return false;
    }
  },

  // 获取特定店铺的入库包裹（已送达的）
  async getDeliveredPackagesByStore(storeId: string): Promise<Package[]> {
    try {
      const { data, error } = await supabase
        .from('packages')
        .select('*')
        .eq('delivery_store_id', storeId)
        .eq('status', '已送达')
        .order('delivery_time', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (err) {
      LoggerService.error(`获取店铺 ${storeId} 包裹失败:`, err);
      return [];
    }
  },

  /** 营收统计（已送达商品费，对齐商家 App HomeScreen） */
  async getRevenueStats(userId: string, storeName?: string) {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yearStart = new Date(today.getFullYear(), 0, 1);
      yearStart.setHours(0, 0, 0, 0);

      const buildQuery = (startDate?: Date, endDate?: Date) => {
        let query = supabase
          .from('packages')
          .select('cod_amount, description')
          .eq('status', '已送达');
        const conditions = [`delivery_store_id.eq.${userId}`];
        if (storeName) conditions.push(`sender_name.eq.${storeName}`);
        query = query.or(conditions.join(','));
        if (startDate) query = query.gte('created_at', startDate.toISOString());
        if (endDate) query = query.lt('created_at', endDate.toISOString());
        return query;
      };

      const sumItemFee = (
        data: { cod_amount?: number | null; description?: string | null }[] | null,
      ) =>
        (data || []).reduce((sum, p) => sum + getProductItemFeeMmkForPackage(p), 0);

      const countRows = (
        data: { cod_amount?: number | null; description?: string | null }[] | null,
      ) => (data || []).length;

      const fetchItemFee = async (start?: Date, end?: Date) => {
        const { data, error } = await buildQuery(start, end);
        if (error) throw error;
        return sumItemFee(data);
      };

      const fetchCount = async (start?: Date, end?: Date) => {
        const { data, error } = await buildQuery(start, end);
        if (error) throw error;
        return countRows(data);
      };

      const [
        todayRevenue,
        yesterdayRevenue,
        revenueOneYear,
        todayOrderCount,
        yesterdayOrderCount,
      ] = await Promise.all([
        fetchItemFee(today, new Date()),
        fetchItemFee(yesterday, today),
        fetchItemFee(yearStart, undefined),
        fetchCount(today, new Date()),
        fetchCount(yesterday, today),
      ]);

      return {
        todayOrderCount,
        yesterdayOrderCount,
        todayRevenue,
        yesterdayRevenue,
        revenueOneYear,
      };
    } catch (error) {
      LoggerService.error('获取营收统计失败:', error);
      return {
        todayOrderCount: 0,
        yesterdayOrderCount: 0,
        todayRevenue: 0,
        yesterdayRevenue: 0,
        revenueOneYear: 0,
      };
    }
  },
};

// 广告服务
// bannerService.getActiveBanners 实现见 /shared/src/services.ts（工厂注入 client + logger）
export const bannerService = createBannerService(supabase, LoggerService);

// 简化的用户服务（客户端使用）
export interface User {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  address?: string;
  password?: string;
  user_type: 'customer' | 'courier';
  created_at?: string;
  updated_at?: string;
  balance?: number; // 🚀 新增：账户余额
}

export const userService = {
  // 获取用户余额
  async getUserBalance(userId: string): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('balance')
        .eq('id', userId)
        .single();
      
      if (error) throw error;
      return data?.balance || 0;
    } catch (error) {
      LoggerService.error('获取用户余额失败:', error);
      return 0;
    }
  },

  // 充值余额
  async rechargeBalance(userId: string, amount: number) {
    try {
      // 1. 获取当前余额
      const currentBalance = await this.getUserBalance(userId);
      const newBalance = currentBalance + amount;

      // 2. 更新余额
      const { data, error } = await supabase
        .from('users')
        .update({ 
          balance: newBalance,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;

      // 3. 记录交易 (待后续完善 transactions 表)
      // await transactionService.create({ user_id: userId, amount, type: 'recharge' });

      return { success: true, data };
    } catch (error: any) {
      LoggerService.error('充值失败:', error);
      return { success: false, error: error.message };
    }
  },

  // 更新用户信息
  async updateUser(userId: string, updates: Partial<User>) {
    try {
      const { data, error } = await supabase
        .from('users')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error: any) {
      LoggerService.error('更新用户信息失败:', error);
      return { success: false, error };
    }
  },
  // 根据手机号获取用户
  async getUserByPhone(phone: string): Promise<User | null> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('phone', phone)
        .eq('user_type', 'customer')
        .single();
      
      if (error || !data) {
        return null;
      }
      return data;
    } catch (err) {
      LoggerService.error('获取用户失败:', err);
      return null;
    }
  },

  // 创建客户
  async createCustomer(userData: {
    name: string;
    phone?: string;
    email?: string;
    address?: string;
    password?: string;
  }): Promise<User | null> {
    try {
      // 明确只提取需要的字段，防止多余字段导致插入失败
      const { name, phone, email, address, password } = userData;
      
      // 生成唯一ID，格式与后台系统保持一致 (USR + 6位随机数/时间戳)
      const newId = `USR${Date.now().toString().slice(-6)}${Math.floor(Math.random() * 100)}`;
      
      const cleanData = {
        id: newId,
        name,
        phone: phone || '',
        email: email || '',
        address: address || '',
        password: password || '123456',
        user_type: 'customer' as const,
        registration_date: new Date().toLocaleDateString('zh-CN'),
        last_login: '从未登录',
        status: 'active',
        // 补充缺失的必须字段，防止数据库报错
        total_orders: 0,
        total_spent: 0,
        rating: 5,
        notes: '通过 Web 注册'
      };

      LoggerService.debug('尝试在数据库创建新用户:', cleanData);

      const { data, error } = await supabase
        .from('users')
        .insert([cleanData])
        .select()
        .single();
      
      if (error) {
        LoggerService.error('创建用户失败 (数据库错误):', error);
        // 如果是因为 ID 冲突，再试一次
        if (error.code === '23505') {
          const retryId = `USR${Date.now().toString().slice(-6)}${Math.floor(Math.random() * 1000)}`;
          cleanData.id = retryId;
          const { data: retryData, error: retryError } = await supabase
            .from('users')
            .insert([cleanData])
            .select()
            .single();
          if (retryError) {
            LoggerService.error('重试创建用户依然失败:', retryError);
            return null;
          }
          return retryData;
        }
        return null;
      }
      return data;
    } catch (err) {
      LoggerService.error('创建用户异常 (服务层):', err);
      return null;
    }
  },

  // 更新用户统计（简化版）
  async updateUserStats(userId: string, points: number): Promise<boolean> {
    // 客户端不需要更新统计，直接返回 true
    return true;
  }
};

// 临时订单服务（客户端使用）
export interface PendingOrder {
  id?: string;
  temp_order_id: string;
  sender_name: string;
  sender_phone: string;
  sender_address: string;
  sender_latitude?: number | null;
  sender_longitude?: number | null;
  receiver_name: string;
  receiver_phone: string;
  receiver_address: string;
  receiver_latitude?: number | null;
  receiver_longitude?: number | null;
  package_type: string;
  weight: string;
  delivery_speed?: string | null;
  scheduled_delivery_time?: string | null;
  price: number;
  distance: number;
  payment_method: 'qr' | 'cash' | 'balance';
  cod_amount?: number; // 代收款金额
  description?: string; // 🚀 新增：物品描述
  customer_email?: string | null;
  customer_name?: string | null;
  created_at?: string;
  expires_at?: string;
}

export const pendingOrderService = {
  // 创建临时订单
  async createPendingOrder(orderData: Omit<PendingOrder, 'id' | 'created_at' | 'expires_at'>): Promise<PendingOrder | null> {
    try {
      const { data, error } = await supabase
        .from('pending_orders')
        .insert([{
          ...orderData,
          id: orderData.temp_order_id // 使用temp_order_id作为主键
        }])
        .select()
        .single();
      
      if (error) {
        LoggerService.error('创建临时订单失败:', error);
        return null;
      }
      return data;
    } catch (err) {
      LoggerService.error('创建临时订单异常:', err);
      return null;
    }
  },

  // 根据临时订单ID获取订单
  async getPendingOrderByTempId(tempOrderId: string): Promise<PendingOrder | null> {
    try {
      const { data, error } = await supabase
        .from('pending_orders')
        .select('*')
        .eq('temp_order_id', tempOrderId)
        .single();
      
      if (error || !data) {
        return null;
      }
      return data;
    } catch (err) {
      LoggerService.error('获取临时订单异常:', err);
      return null;
    }
  },

  // 删除临时订单
  async deletePendingOrder(tempOrderId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('pending_orders')
        .delete()
        .eq('temp_order_id', tempOrderId);
      
      if (error) {
        LoggerService.error('删除临时订单失败:', error);
        return false;
      }
      return true;
    } catch (err) {
      LoggerService.error('删除临时订单异常:', err);
      return false;
    }
  }
};

const DELIVERY_STORE_SELECT_WITHOUT_AVATAR =
  'id, store_name, store_code, address, latitude, longitude, phone, email, manager_name, manager_phone, store_type, status, operating_hours, service_area_radius, capacity, current_load, facilities, notes, created_by, created_at, updated_at, region, is_closed_today, current_session_id, cod_settlement_day, vacation_dates, mall_visible, packing_sla_minutes';

function isDeliveryStoreColumnDenied(error: any): boolean {
  const text = `${error?.code || ''} ${error?.message || ''} ${error?.details || ''}`;
  return /42501|permission denied|avatar_url/i.test(text);
}

async function queryDeliveryStores<T>(
  run: (columns: string) => PromiseLike<{ data: T; error: any }>,
): Promise<{ data: T; error: any }> {
  const first = await run('*');
  if (first.error && isDeliveryStoreColumnDenied(first.error)) {
    return run(DELIVERY_STORE_SELECT_WITHOUT_AVATAR);
  }
  return first;
}

function isCityMallVisibleStore(store: Record<string, unknown>): boolean {
  if (store.mall_visible === false) return false;
  if (store.store_type === 'transit_station') return false;
  const addr = String(store.address ?? '');
  const notes = String(store.notes ?? '');
  if (/跨境物流中转站|cross-border transit hub/i.test(addr)) return false;
  if (/Inventory App 跨境/i.test(notes)) return false;
  return true;
}

// 配送店服务
export const deliveryStoreService = {
  async getActiveStores() {
    try {
      let { data, error } = await queryDeliveryStores((columns) =>
        supabase
          .from('delivery_stores')
          .select(columns as '*')
          .eq('status', 'active')
          .eq('mall_visible', true)
          .order('store_name', { ascending: true }),
      );
      if (error?.message?.includes('mall_visible')) {
        const fallback = await queryDeliveryStores((columns) =>
          supabase
            .from('delivery_stores')
            .select(columns as '*')
            .eq('status', 'active')
            .neq('store_type', 'transit_station')
            .order('store_name', { ascending: true }),
        );
        data = fallback.data;
        error = fallback.error;
      }

      if (error) throw error;
      return (data || []).filter(isCityMallVisibleStore);
    } catch (error) {
      LoggerService.error('获取配送店列表失败:', error);
      return [];
    }
  },

  async getStoreById(storeId: string) {
    try {
      const { data, error } = await queryDeliveryStores((columns) =>
        supabase.from('delivery_stores').select(columns as '*').eq('id', storeId).maybeSingle(),
      );

      if (error) throw error;
      const store = data as DeliveryStore | null;
      if (store && !('avatar_url' in store)) {
        const avatar = await supabase
          .from('delivery_stores')
          .select('avatar_url')
          .eq('id', storeId)
          .maybeSingle();
        if (avatar.data?.avatar_url) {
          return { ...store, avatar_url: avatar.data.avatar_url };
        }
      }
      return store;
    } catch (error) {
      LoggerService.error('获取店铺详情失败:', error);
      return null;
    }
  },

  async updateStoreInfo(storeId: string, updates: Partial<DeliveryStore>) {
    const allowedKeys = [
      'store_name',
      'phone',
      'email',
      'address',
      'operating_hours',
      'is_closed_today',
      'vacation_dates',
      'avatar_url',
    ] as const;

    const payload: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    };
    for (const key of allowedKeys) {
      const value = updates[key as keyof DeliveryStore];
      if (value !== undefined) {
        payload[key] = value;
      }
    }

    if (Object.keys(payload).length <= 1) {
      return {
        success: false,
        error: new Error('No valid fields to update'),
      };
    }

    const runUpdate = async (body: Record<string, unknown>) => {
      const { data, error } = await queryDeliveryStores((columns) =>
        supabase.from('delivery_stores').update(body).eq('id', storeId).select(columns as '*'),
      );

      if (error) throw error;
      if (!data?.length) {
        throw new Error('STORE_UPDATE_NO_ROWS');
      }
      return data[0] as DeliveryStore;
    };

    try {
      const data = await runUpdate(payload);
      return { success: true, data };
    } catch (error: any) {
      const message = String(error?.message || error?.details || '');
      if (payload.avatar_url !== undefined && /avatar_url|PGRST204|schema cache|permission denied|42501/i.test(message)) {
        return {
          success: false,
          error: { ...error, code: 'NO_AVATAR_COLUMN' },
        };
      }

      const missingOptionalColumn =
        message.includes('is_closed_today') ||
        message.includes('vacation_dates') ||
        error?.code === '42703';

      if (
        missingOptionalColumn &&
        (payload.is_closed_today !== undefined ||
          payload.vacation_dates !== undefined)
      ) {
        const fallback = { ...payload };
        delete fallback.is_closed_today;
        delete fallback.vacation_dates;
        delete fallback.avatar_url;
        try {
          const data = await runUpdate(fallback);
          LoggerService.warn(
            'delivery_stores 缺少 is_closed_today/vacation_dates 列，已仅保存营业时间',
          );
          return { success: true, data, partial: true as const };
        } catch (fallbackError: any) {
          LoggerService.error('更新商店信息失败(降级后仍失败):', fallbackError);
          return { success: false, error: fallbackError };
        }
      }

      LoggerService.error('更新商店信息失败:', error);
      return { success: false, error };
    }
  },

  async uploadStoreAvatar(storeId: string, file: File): Promise<string | null> {
    if (!storeId || !file) return null;
    const fileName = `${storeId}/avatar.jpg`;
    const buckets = ['product_images', 'review_images'] as const;
    let lastError: unknown = null;

    for (const bucket of buckets) {
      const { error } = await supabase.storage.from(bucket).upload(fileName, file, {
        contentType: file.type || 'image/jpeg',
        cacheControl: '60',
        upsert: true,
      });
      if (error) {
        lastError = error;
        LoggerService.warn(`店铺头像上传到 ${bucket}/${fileName} 失败:`, error);
        continue;
      }
      const {
        data: { publicUrl },
      } = supabase.storage.from(bucket).getPublicUrl(fileName);
      return rewritePublicStorageUrl(publicUrl);
    }

    LoggerService.error('上传店铺头像失败:', lastError);
    return null;
  },

  async removeStoreAvatar(storeId: string): Promise<void> {
    await this.updateStoreInfo(storeId, { avatar_url: '' });
    for (const bucket of ['product_images', 'review_images'] as const) {
      try {
        await supabase.storage.from(bucket).remove([`${storeId}/avatar.jpg`]);
      } catch (err) {
        LoggerService.warn(`删除 ${bucket} 店铺头像失败:`, err);
      }
    }
  },
};

// 商家服务
export const merchantService = {
  // 获取商店的所有商品
  async getStoreProducts(storeId: string): Promise<Product[]> {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('store_id', storeId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      LoggerService.error('获取商店商品失败:', error);
      return [];
    }
  },

  // 获取商店分类
  async getStoreCategories(storeId: string): Promise<ProductCategory[]> {
    try {
      const { data, error } = await supabase
        .from('product_categories')
        .select('*')
        .eq('store_id', storeId)
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      LoggerService.error('获取商店分类失败:', error);
      return [];
    }
  },

  // —— 商品上架审核：见文件顶部 productNeedsAdminReview / pickProductReviewSnapshot ——

  /** 新建商品：始终待审，客户端不可见直至 Admin 通过 */
  async addProduct(product: Omit<Product, 'id' | 'created_at' | 'updated_at' | 'sales_count'>) {
    try {
      const {
        listing_status: _ls,
        sales_count: _sc,
        pending_update: _pu,
        ...rest
      } = product as Product;
      const { data, error } = await supabase
        .from('products')
        .insert([
          {
            ...rest,
            sales_count: 0,
            listing_status: 'pending' as const,
            is_available: false,
            pending_update: null,
          },
        ])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data, pendingReview: true as const };
    } catch (error: any) {
      LoggerService.error('添加商品失败:', error);
      return { success: false, error };
    }
  },

  /** 内部直写主表（库存/上下架快捷操作勿直接调用，请用 submitMerchantProductChange） */
  async updateProduct(productId: string, updates: Partial<Product>) {
    try {
      const { pending_update: _pu, ...safeUpdates } = updates as Partial<Product> & {
        pending_update?: unknown;
      };
      const { data, error } = await supabase
        .from('products')
        .update({
          ...safeUpdates,
          updated_at: new Date().toISOString()
        })
        .eq('id', productId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error: any) {
      LoggerService.error('更新商品失败:', error);
      return { success: false, error };
    }
  },

  /**
   * 商家侧任意商品资料变更（编辑表单 / 上下架 / 改价等）统一入口。
   * 已上架 → pending_update；未上架 → 直写主表并保持 pending。
   */
  async submitMerchantProductChange(
    product: Product,
    changes: Partial<ProductPendingUpdate> & Record<string, unknown>,
  ) {
    try {
      const snapshot = buildPendingUpdateFromProduct(product, normalizePendingPayload(changes));

      if (isProductLiveApproved(product.listing_status)) {
        const pending_update: ProductPendingUpdate = {
          ...snapshot,
          submitted_at: new Date().toISOString(),
        };
        const { data, error } = await supabase
          .from('products')
          .update({
            pending_update,
            updated_at: new Date().toISOString(),
          })
          .eq('id', product.id)
          .select()
          .single();
        if (error) throw error;
        return { success: true, data, pendingReview: true as const };
      }

      const payload: Partial<Product> = {
        ...toDirectProductPatch(snapshot),
        updated_at: new Date().toISOString(),
        pending_update: null,
      };
      const ls = (product.listing_status ?? 'pending').trim();
      if (ls === 'rejected') {
        payload.listing_status = 'pending';
      }
      return this.updateProduct(product.id, payload);
    } catch (error: any) {
      LoggerService.error('提交商品变更失败:', error);
      return { success: false, error };
    }
  },

  /** @deprecated 请用 saveMerchantProduct 或 submitMerchantProductChange */
  async submitProductEdit(
    productId: string,
    updates: Partial<ProductPendingUpdate> & Record<string, unknown>,
    listingStatus?: string | null,
  ) {
    const { data: row } = await supabase.from('products').select('*').eq('id', productId).single();
    if (!row) return { success: false, error: { message: 'Product not found' } };
    const product = row as Product;
    if (listingStatus !== undefined) {
      product.listing_status = listingStatus as Product['listing_status'];
    }
    return this.submitMerchantProductChange(product, updates);
  },

  /** 添加/编辑商品表单保存 */
  async saveMerchantProduct(params: {
    mode: 'create' | 'edit';
    product?: Product | null;
    storeId: string;
    draft: Partial<ProductPendingUpdate> & Record<string, unknown>;
  }) {
    const snapshot = normalizePendingPayload(params.draft);
    if (params.mode === 'create') {
      return this.addProduct({
        store_id: params.storeId,
        ...toDirectProductPatch(snapshot),
        sales_count: 0,
      } as Omit<Product, 'id' | 'created_at' | 'updated_at' | 'sales_count'>);
    }
    if (!params.product) {
      return { success: false, error: { message: 'Product required for edit' } };
    }
    return this.submitMerchantProductChange(params.product, snapshot);
  },

  async toggleAvailability(product: Product) {
    return this.submitMerchantProductChange(product, { is_available: !product.is_available });
  },

  async updateStock(product: Product, newStock: number) {
    return this.submitMerchantProductChange(product, { stock: newStock });
  },

  // 删除商品
  async deleteProduct(productId: string) {
    try {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', productId);

      if (error) throw error;
      return { success: true };
    } catch (error: any) {
      LoggerService.error('删除商品失败:', error);
      return { success: false, error };
    }
  },

  // 🚀 新增：搜索商品 (Web版)
  async searchProductsByName(query: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          delivery_stores (
            id,
            store_name,
            address,
            phone,
            store_type,
            operating_hours,
            is_closed_today
          )
        `)
        .ilike('name', `%${query}%`)
        .eq('is_available', true)
        .limit(40);

      if (error) throw error;
      const rows = (data || []).filter((row) => {
        const store = row.delivery_stores as { store_type?: string; mall_visible?: boolean } | null | undefined;
        if (!store || store.mall_visible === false || store.store_type === 'transit_station') return false;
        const ls = (row.listing_status ?? '').toString().trim();
        if (ls === 'pending' || ls === 'rejected') return false;
        return ls === 'approved' || ls === '';
      }).slice(0, 20);
      return rows;
    } catch (error: any) {
      LoggerService.error('搜索商品失败:', error?.message || '未知错误');
      return [];
    }
  },

  // 上传商品图片 (Web版)
  async uploadProductImage(storeId: string, file: File): Promise<string | null> {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${storeId}/${Date.now()}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('product_images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) throw error;

      // 获取公共 URL
      const { data: { publicUrl } } = supabase.storage
        .from('product_images')
        .getPublicUrl(fileName);

      return rewritePublicStorageUrl(publicUrl);
    } catch (error: any) {
      LoggerService.error('上传商品图片失败:', error?.message || '未知错误');
      return null;
    }
  }
};

// 系统设置服务（客户端使用）；计费合并算法见 /shared/src/pricing.ts，输出 camelCase
export const systemSettingsService = {
  async getPricingSettings(region?: string) {
    const defaults: Record<string, number> = {
      baseFee: 1500,
      perKmFee: 250,
      weightSurcharge: 150,
      urgentSurcharge: 500,
      oversizeSurcharge: 300,
      scheduledSurcharge: 200,
      fragileSurcharge: 300,
      foodBeverageSurcharge: 300,
      freeKmThreshold: 3,
      waySideCourierPerOrder: 0,
    };

    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('settings_key, settings_value')
        .like('settings_key', 'pricing.%');

      if (error) throw error;

      return buildPricingSettings(data, region, {
        defaults,
        toField: pricingFieldToCamel,
      });
    } catch (error) {
      LoggerService.error('获取计费设置异常:', error);
      return { ...defaults };
    }
  }
};

// 充值服务
export const rechargeService = {
  // 上传支付凭证 (Web版使用 File)
  async uploadProof(userId: string, file: File): Promise<string | null> {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `recharge_${userId}_${Date.now()}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('payment_proofs')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) throw error;

      // 获取公共 URL
      const { data: { publicUrl } } = supabase.storage
        .from('payment_proofs')
        .getPublicUrl(fileName);

      return publicUrl;
    } catch (error: any) {
      LoggerService.error('上传凭证失败:', error?.message || '未知错误');
      return null;
    }
  },

  // 创建充值申请
  async createRequest(requestData: Omit<RechargeRequest, 'id' | 'created_at' | 'updated_at'>) {
    try {
      const { data, error } = await supabase
        .from('recharge_requests')
        .insert([{
          ...requestData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error: any) {
      LoggerService.error('创建充值申请失败:', error?.message || '未知错误');
      return { success: false, error };
    }
  }
};

// 评价服务
export const reviewService = {
  // 提交评价
  async createReview(reviewData: Omit<StoreReview, 'id' | 'created_at' | 'updated_at' | 'status'>) {
    try {
      const { data, error } = await supabase
        .from('store_reviews')
        .insert([{
          ...reviewData,
          status: 'published', // 默认发布，后期可改为 pending 进入审核流
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error: any) {
      LoggerService.error('提交评价失败:', error?.message || '未知错误');
      return { success: false, error };
    }
  },

  // 获取店铺评价列表
  async getStoreReviews(storeId: string) {
    try {
      const { data, error } = await supabase
        .from('store_reviews')
        .select('*')
        .eq('store_id', storeId)
        .eq('status', 'published')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      LoggerService.error('获取评价列表失败:', error?.message || '未知错误');
      return [];
    }
  },

  // 获取店铺评分统计
  async getStoreReviewStats(storeId: string) {
    try {
      const { data, error } = await supabase
        .from('store_reviews')
        .select('rating')
        .eq('store_id', storeId)
        .eq('status', 'published');

      if (error) throw error;
      
      if (!data || data.length === 0) {
        return { average: 0, count: 0, distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 } };
      }

      const count = data.length;
      const sum = data.reduce((acc, curr) => acc + curr.rating, 0);
      const average = parseFloat((sum / count).toFixed(1));
      
      const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
      data.forEach(r => {
        const rating = r.rating as keyof typeof distribution;
        distribution[rating] = (distribution[rating] || 0) + 1;
      });

      return { average, count, distribution };
    } catch (error: any) {
      LoggerService.error('获取评价统计失败:', error?.message || '未知错误');
      return { average: 0, count: 0, distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 } };
    }
  },

  // 商家回复评价
  async replyToReview(reviewId: string, replyText: string) {
    try {
      const { data, error } = await supabase
        .from('store_reviews')
        .update({
          reply_text: replyText,
          replied_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', reviewId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error: any) {
      LoggerService.error('回复评价失败:', error?.message || '未知错误');
      return { success: false, error };
    }
  },

  // 上传评价图片
  async uploadReviewImage(userId: string, file: File): Promise<string | null> {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `review_${userId}_${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('review_images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('review_images')
        .getPublicUrl(fileName);

      return publicUrl;
    } catch (error: any) {
      LoggerService.error('上传评价图片失败:', error?.message || '未知错误');
      return null;
    }
  }
};

// 🚀 新增：使用教学服务
// tutorialService.getAllTutorials 实现见 /shared/src/services.ts
export const tutorialService = createTutorialService(supabase, LoggerService);

// 测试连接（简化版）
export const testConnection = async (): Promise<boolean> => {
  try {
    const { error } = await supabase.from('packages').select('id').limit(1);
    return !error;
  } catch {
    return false;
  }
};

